/**
 * Notes Component
 * 
 * A rich text editor component for managing notes within the application.
 * Built on top of ReactQuill to provide a full-featured WYSIWYG editing experience
 * with a clean, user-friendly interface for both viewing and editing modes.
 * 
 * Usage in Project:
 * 1. Travel Details (/src/components/travel/TravelDetails/TravelDetails.js):
 *    - Used to add and edit notes for travel items (flights, trains, buses)
 *    - Integrated with travel state management
 *    - Notes are stored in travel.notes and managed through handleSaveNotes
 * 
 * 2. Place Details (/src/PlaceDetails.js):
 *    - Used for rich text notes about places of interest
 *    - Notes stored in place.userContent
 *    - Integrated with place state management and persistence
 * 
 * 3. Accommodation Details (/src/components/accommodation/AccommodationDetails/AccommodationDetails.js):
 *    - Used for detailed notes about accommodations (hotels, apartments, etc.)
 *    - Notes stored in accommodation.notes
 *    - Integrated with accommodation state management
 * 
 * Important Note:
 * This rich text editor component is ONLY used in the Details views of Travel, Places,
 * and Accommodation. It should NOT be used in Add/Edit forms for these items, where
 * simpler note fields may be more appropriate. The Add forms will be updated to remove
 * additional notes fields to maintain consistency.
 * 
 * Integration Points:
 * - Connects with respective services (travel, places, accommodation) for data persistence
 * - Maintains edit history through lastEditedTime
 * - Supports both inline editing and form-based notes
 * 
 * Features:
 * - Rich text editing with formatting options
 * - View/Edit mode toggle
 * - Unsaved changes tracking
 * - Last edited timestamp display
 * - Save/Cancel functionality
 * - Responsive design
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.content=''] - The HTML content of the notes
 * @param {Function} props.onSave - Callback function to handle saving notes content
 * @param {Date} [props.lastEditedTime] - Timestamp of the last edit
 * @param {string} [props.className=''] - Additional CSS classes for styling
 */

import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Notes.css';

const Notes = ({ 
  content = '',
  onSave,
  lastEditedTime,
  className = ''
}) => {
  /**
   * Component State Management
   * 
   * The Notes component manages three key pieces of state:
   * 
   * 1. Edit Mode State:
   *    - isEditing: Controls whether the component is in view or edit mode
   *    - Toggled by Edit/Save/Cancel actions
   *    - Parent components can't directly control edit mode
   * 
   * 2. Content State:
   *    - tempContent: Holds temporary content during editing
   *    - Initialized with props.content
   *    - Updated through ReactQuill onChange events
   *    - Only persisted to parent on successful save
   * 
   * 3. Change Tracking:
   *    - hasUnsavedChanges: Tracks whether current content differs from saved
   *    - Used to enable/disable save button
   *    - Reset on successful save or cancel
   * 
   * Data Flow:
   * 1. Parent -> Component:
   *    - Initial content through content prop
   *    - Last edit time through lastEditedTime prop
   *    - Save handler through onSave prop
   * 
   * 2. Component -> Parent:
   *    - Updated content via onSave callback
   *    - Error propagation through save operation
   * 
   * 3. Internal Flow:
   *    - Editor changes -> tempContent -> hasUnsavedChanges
   *    - Save success -> update parent -> reset internal state
   *    - Cancel -> reset to initial content -> reset internal state
   */
  const [isEditing, setIsEditing] = useState(false);
  const [tempContent, setTempContent] = useState(content);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  /**
   * ReactQuill Editor Configuration
   * 
   * Defines the available formatting options and toolbar layout.
   * These settings are used consistently across all note instances
   * in the travel management system.
   * 
   * Integration:
   * - Used in travel forms for consistent note formatting
   * - Maintains formatting when editing existing notes
   * - Ensures HTML output is compatible with travel service storage
   * 
   * Toolbar includes:
   * - Text formatting (bold, italic, underline)
   * - Text alignment
   * - Headers
   * - Lists (ordered and bullet)
   * - Indentation
   * - Links and images
   */
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'align': ['', 'center', 'right', 'justify'] }],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image']
    ]
  };

  /**
   * Allowed formatting options for the editor
   * These formats correspond to the toolbar options
   * and define what types of formatting are permitted.
   * 
   * Note: These formats are validated by the travel service
   * before storage to ensure data consistency.
   */
  const formats = [
    'bold', 'italic', 'underline',
    'align', 'header',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  /**
   * Handles changes to the editor content
   * Updates temporary content and marks changes as unsaved
   * 
   * Integration Flow:
   * 1. User edits content in ReactQuill
   * 2. handleContentChange receives HTML content
   * 3. Content is stored in tempContent state
   * 4. Changes are marked as unsaved
   * 5. Save button is enabled
   * 
   * @param {string} newContent - The new HTML content from the editor
   */
  const handleContentChange = (newContent) => {
    setTempContent(newContent);
    setHasUnsavedChanges(true);
  };

  /**
   * Handles saving the notes content
   * Calls the onSave callback with new content
   * Updates component state on successful save
   * 
   * Integration with Travel Service:
   * 1. Component calls onSave with new content
   * 2. Parent (TravelDetails) updates travel.notes
   * 3. Travel service persists changes
   * 4. LastEditedTime is updated
   * 5. Edit mode is exited
   * 
   * @async
   * @throws {Error} When save operation fails
   */
  const handleSave = async () => {
    try {
      await onSave(tempContent);
      setIsEditing(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save notes:', error);
      // Could add error handling here or propagate to parent
    }
  };

  /**
   * Handles canceling edit mode
   * Reverts content to last saved state
   * Resets editing state and unsaved changes
   * 
   * Used in:
   * - Manual cancel via button
   * - Navigation away from travel details
   * - Error recovery
   */
  const handleCancel = () => {
    setTempContent(content);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  return (
    <section className={`notes ${className}`}>
      {/* Header Section */}
      <div className="notes-section-header">
        <h2>Notes</h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="edit-notes-button"
          >
            Edit Notes
          </button>
        )}
      </div>

      <div className="notes-content">
        {isEditing ? (
          // Editor Mode
          <div className="notes-editor-container">
            {/* Top Action Buttons */}
            <div className="notes-button-group">
              <button
                onClick={handleCancel}
                className="cancel-notes-button"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="save-notes-button"
                disabled={!hasUnsavedChanges}
              >
                Save Notes
              </button>
            </div>

            {/* Rich Text Editor */}
            <ReactQuill
              value={tempContent}
              onChange={handleContentChange}
              modules={modules}
              formats={formats}
              className="editor"
            />

            {/* Bottom Action Bar with Save Status */}
            <div className="notes-footer">
              {lastEditedTime && (
                <div className="last-edited">
                  Last saved: {lastEditedTime.toLocaleString()}
                </div>
              )}
              <button
                onClick={handleSave}
                className="save-notes-button"
                disabled={!hasUnsavedChanges}
              >
                Save Notes
              </button>
              <button
                onClick={handleCancel}
                className="cancel-notes-button"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // View Mode
          <div className="notes-display">
            {content ? (
              // Display existing notes content
              <div
                className="notes-content"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              // Empty state message
              <div className="notes-placeholder">
                No notes yet. Click Edit Notes to add notes.
              </div>
            )}
            {/* Last edited timestamp */}
            {lastEditedTime && (
              <div className="last-edited">
                Last edited: {lastEditedTime.toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Notes;